import { SearchInput } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { AnalyticsMenu, AnalyticsMenuAction } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import { Fragment, memo, useCallback, useMemo } from 'react';

import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { useDashboardLastRefresh } from '../../../../utils/useDashboardLastRefresh';

import { QueueDashboardFilterControl } from './components/filter/queueDashboardFilterControl';
import { QueueDashboardPreferencesControl } from './components/preferences/queueDashboardPreferencesControl';

export const QueueDashboardControls = memo(
  function QueueDashboardControls(): JSX.Element {
    const dispatch = useAppDispatch();
    const flags = useFeatureFlag(['dashboard-aggregate-favorites']);

    const { params, patchParams, unsetParams } =
      useAppSearchParams('/dashboard/queues');

    const handleSearchToggle = useCallback(
      (isOpen: boolean) => {
        if (!isOpen && params.q) {
          unsetParams(['q', 'page']);
          ampli.dashboardQueuesSearchFieldCleared({ location: 'page' });
        }
      },
      [params.q, unsetParams],
    );

    const handleSearchSubmit = useCallback(
      (value: string) => {
        if (value) {
          patchParams({
            set: {
              q: value,
            },
            unset: ['page'],
          });
          ampli.dashboardQueuesSearchFieldSubmitted({ location: 'page' });
        } else {
          unsetParams(['q', 'page']);
          ampli.dashboardQueuesSearchFieldCleared({ location: 'page' });
        }
      },
      [patchParams, unsetParams],
    );

    const { refreshAction } = useDashboardLastRefresh({
      dashboardType: 'queue',
    });

    const actions = useMemo(
      (): AnalyticsMenuAction[] => [
        refreshAction,
        {
          type: 'divider',
        },
        {
          type: 'link',
          label: 'TV mode',
          icon: 'presentation',
          link:
            flags.dashboardAggregateFavorites &&
            params.type === 'favourite' &&
            params.id ? (
              <AppLink
                path="/tv/dashboard/favorites/:id"
                params={{ path: { id: params.id } }}
              />
            ) : (
              <AppLink path="/tv/dashboard/organization" />
            ),
          onClick: () => {
            ampli.dashboardGlobalTvDashboardClicked();
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'button',
          label: 'Send feedback',
          icon: 'paper-plane',
          onClick: () => {
            dispatch(
              appActions.showModal({
                modal: {
                  type: 'app:feedbackForm',
                  arg: {
                    title: 'Dashboard Feedback',
                    team: 'insights',
                    extra: {
                      dashboard: 'queues',
                    },
                  },
                },
              }),
            );
          },
        },
        {
          type: 'button',
          label: 'Help and documentation',
          icon: 'question',
          onClick: () => {
            elevioClient.openArticle(articles.dashboard.queues.documentation);
          },
        },
      ],
      [
        dispatch,
        flags.dashboardAggregateFavorites,
        params.id,
        params.type,
        refreshAction,
      ],
    );

    return (
      <Fragment>
        <SearchInput
          initialSearch={params.q}
          onToggle={handleSearchToggle}
          onSubmit={handleSearchSubmit}
        />

        <QueueDashboardFilterControl />

        <QueueDashboardPreferencesControl />

        <AnalyticsMenu actions={actions} />
      </Fragment>
    );
  },
);
